import { useContext, useEffect, useRef, useState } from "react";
import { Pencil, UserPlus, Trash2, Loader2, Bell } from "lucide-react";
import AuthContext from "../context/AuthContext";
import {
  cancelOccurrence,
  deleteEvent,
  deleteEventRsvp,
  fetchEvent,
  fetchOccurrence,
  fetchOccurrences,
  inviteToEvent,
  inviteToOccurrence,
  submitRsvp,
  updateReminder,
} from "../api/EventAPI";
import EventCreateForm from "./EventCreateForm";
import EventDetailHeader from "./EventDetailHeader";
import { GuestList, GuestSummary, guestStats } from "./EventGuests";
import OccurrenceEditForm from "./OccurrenceEditForm";
import OccurrenceOnlyDetail from "./OccurrenceOnlyDetail";
import { useModal } from "./ModalManager";
import {
  REMINDER_OPTIONS,
  REMOVAL,
  effectiveOccurrence,
  eventDetailActions,
  eventDetailLayout,
  isCanceled,
  isRecurring,
  myReminder,
  myRsvpStatus,
  removalConfirmation,
  removalMenuLabel,
  seriesEditOccurrence,
} from "../utils/eventDomain";
import { errorCode, eventErrorMessage, isNotFound } from "../utils/eventErrors";
import { announceEventChange } from "../utils/eventChanges";
import { INVITE_SCOPE, inviteOutcome, inviteSummary } from "../utils/eventInvites";
import { answerOccurrence, loadEventDetailAccess } from "../utils/occurrenceActions";
import { occurrenceParticipation, rebaseOnSeriesRsvp, reminderEdit, reminderFooter } from "../utils/occurrenceParticipation";
import { addDays, formatDay, startOfDayInZone } from "../utils/eventTime";

const RSVP_OPTIONS = [
  { value: "going", label: "Going" },
  { value: "declined", label: "Can't go" },
];

const reminderLabel = (value) => REMINDER_OPTIONS.find((o) => o.value === value)?.label ?? value;

// "Monday, October 19" for the invite scope question.
function formatInviteDate(details) {
  const date = new Date(details.starts_at);
  if (Number.isNaN(date.getTime())) return "this date";
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    ...(details.is_all_day && details.timezone ? { timeZone: details.timezone } : {}),
  });
}

// The occurrence that represents `previous` after an Event-level edit. IDs
// usually survive (series edits update rows in place; stopping recurrence
// keeps the edited occurrence as the primary), so the same id is re-read
// first. If it's gone (regenerated), the replacement is the event's
// scheduled occurrence for the same series slot (slot_date) — looked up in a
// few days around the old start, well inside the backend's 200-day window.
// Returns null when it can't be identified; the view then shows the Event.
export async function findOccurrenceAfterEdit(event, previous, token) {
  try {
    const same = await fetchOccurrence(previous.id, token);
    if (same.event_id === event.id) return same;
  } catch (err) {
    if (!isNotFound(err)) throw err;
  }

  if (!isRecurring(event)) {
    if (event.primary_occurrence_id == null) return null;
    try {
      return await fetchOccurrence(event.primary_occurrence_id, token);
    } catch (err) {
      if (isNotFound(err)) return null;
      throw err;
    }
  }

  if (!previous.slot_date) return null;
  const candidates = await fetchOccurrences(token, {
    start: startOfDayInZone(addDays(previous.slot_date, -2), event.timezone),
    end: startOfDayInZone(addDays(previous.slot_date, 3), event.timezone),
  });
  return (
    candidates.find(
      (o) => o.event_id === event.id && o.slot_date === previous.slot_date && !isCanceled(o)
    ) || null
  );
}

// Focus/detail view for one Event at one occurrence, hosted in the shared
// ModalManager stack. Shows the occurrence's effective values (its overrides
// over the series), and offers the actions its role allows — mirrors iOS
// CalendarEventDetailView / EventDetailActions / EventDetailLayout:
//   series occurrence — edit This Occurrence / Entire Series; Cancel This
//                       Occurrence or Delete This Series
//   retained          — edit/cancel it alone, or delete the whole Event
//   one-off / former series' own occurrence — Event-level edit and delete
//   canceled          — read-only for everyone
// A series guest declines this date or the series (the trash button's
// choices; a date declined on its own offers Change RSVP to Going), answers a
// pending series invitation, and sets the one Reminder row. A guest invited
// to one occurrence only sees OccurrenceOnlyDetail instead: the Event isn't
// fetched for them (loadEventDetailAccess).
//
// Callers identify what to show with whichever of these they have
// (ModalManager props are a snapshot, so the modal resolves the rest):
//   event        — full EventSerializer object (no fetch needed)
//   eventId      — Event id; the full Event is fetched
//   occurrence   — EventOccurrenceSerializer object: the instance in context
//   occurrenceId — occurrence id (e.g. a notification target); the
//                  occurrence is fetched, then its Event if the viewer can
//                  see the series
//
// Every successful mutation is announced with announceEventChange, which is
// how Calendar and Home refresh. `onUpdated(event)` / `onRemoved(eventId)` are
// for openers that keep their own copies (the invite inbox).
export default function EventFocusModal({
  event = null,
  eventId = null,
  occurrence = null,
  occurrenceId = null,
  onUpdated,
  onRemoved,
  onClose,
}) {
  const { token, user } = useContext(AuthContext);
  const { openModal } = useModal();

  const [localEvent, setLocalEvent] = useState(event);
  const [localOccurrence, setLocalOccurrence] = useState(occurrence);
  // "event" (the full Event, at the occurrence) | "occurrenceOnly" (invited
  // to this occurrence only: OccurrenceOnlyDetail) | null while loading.
  const [accessKind, setAccessKind] = useState(event ? "event" : null);
  const [loadError, setLoadError] = useState("");
  // "view" | "edit-event" | "edit-occurrence" | "choose-scope" |
  // "choose-removal" | "confirm-removal" | "series-not-started" |
  // "choose-invite-scope"
  const [mode, setMode] = useState("view");
  const [reminderPending, setReminderPending] = useState(false);
  const [reminderError, setReminderError] = useState("");
  const [rsvpPending, setRsvpPending] = useState(false);
  const [rsvpError, setRsvpError] = useState("");
  const [inviteStatus, setInviteStatus] = useState("");
  const [inviting, setInviting] = useState(false);
  // Friends picked for a recurring Event, awaiting Invite to Series / This
  // Date Only: { userIds, occurrence } — the occurrence fixed when Invite
  // Friends was opened.
  const [pendingInvite, setPendingInvite] = useState(null);
  const [changingToGoing, setChangingToGoing] = useState(false);
  const [changeToGoingError, setChangeToGoingError] = useState("");
  // The removal being confirmed (a REMOVAL value).
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [removalError, setRemovalError] = useState("");
  const [seriesNotStartedDetail, setSeriesNotStartedDetail] = useState("");
  // One-line outcome shown in the view (e.g. after deleting the future).
  const [notice, setNotice] = useState("");
  // Set once a request finds the event/occurrence gone: nothing is actionable.
  const [unavailable, setUnavailable] = useState(false);
  // Which RSVP summary category's user list is being shown, if any
  // ("invited" | "going" | "declined" | null).
  const [rsvpListStatus, setRsvpListStatus] = useState(null);
  // Synchronous guard against double-submitting a removal.
  const removalInFlight = useRef(false);
  // Bumped by every change to the occurrence shown, so a re-read that was
  // already in flight can't put back an older participation.
  const occurrenceSeq = useRef(0);

  const setOccurrence = (next) => {
    occurrenceSeq.current += 1;
    setLocalOccurrence(next);
  };

  // The shown occurrence's current participation and guest list
  // (GET /event-occurrences/{id}/). A failure keeps what's shown.
  const refreshOccurrence = async (id) => {
    if (id == null) return;
    const seq = ++occurrenceSeq.current;
    try {
      const fresh = await fetchOccurrence(id, token);
      if (seq === occurrenceSeq.current) setLocalOccurrence(fresh);
    } catch (err) {
      if (!isNotFound(err)) console.error("Failed to re-read occurrence", err);
    }
  };

  // Resolve whatever wasn't passed in. Runs once per modal instance (props
  // are a snapshot), so the Event detail is fetched at most once on open. An
  // occurrence-only guest gets the occurrence alone — never the Event.
  useEffect(() => {
    if (event) return;
    let canceled = false;

    (async () => {
      try {
        const access = await loadEventDetailAccess({ eventId, occurrence, occurrenceId }, token);
        if (canceled) return;
        if (access.kind === "unavailable") {
          setLoadError("This event is no longer available.");
          return;
        }
        setOccurrence(access.occurrence ?? null);
        if (access.kind === "occurrenceOnly") {
          setAccessKind("occurrenceOnly");
          return;
        }
        setLocalEvent(access.event);
        setAccessKind("event");
        // A list payload has no guest list: read this date's.
        if (access.occurrence && !Array.isArray(access.occurrence.rsvps)) {
          refreshOccurrence(access.occurrence.id);
        }
      } catch (err) {
        console.error("Failed to load event", { eventId, occurrenceId, err });
        if (!canceled) setLoadError("Couldn't load this event. Please try again.");
      }
    })();

    return () => {
      canceled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (accessKind === "occurrenceOnly" && localOccurrence) {
    return <OccurrenceOnlyDetail occurrence={localOccurrence} onOccurrenceChange={setOccurrence} onClose={onClose} />;
  }

  if (!localEvent || unavailable) {
    return (
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
      >
        {localOccurrence && (
          <h2 className="text-lg font-bold mb-2">
            {effectiveOccurrence({ event: localEvent, occurrence: localOccurrence }).title}
          </h2>
        )}
        {unavailable ? (
          <p className="text-sm text-gray-600">This event is no longer available.</p>
        ) : loadError ? (
          <p className="text-sm text-gray-600">{loadError}</p>
        ) : (
          <div className="flex justify-center py-6 text-gray-400">
            <Loader2 size={22} className="animate-spin" />
          </div>
        )}
      </div>
    );
  }

  const details = effectiveOccurrence({ event: localEvent, occurrence: localOccurrence });
  const rsvpStatus = myRsvpStatus(localEvent);

  // Mirrors the backend's own permission rule (created_by === request.user
  // for edits, invites, cancellation and deletion).
  const isOwner = Boolean(user) && localEvent.created_by === user.id;
  const actions = eventDetailActions({ event: localEvent, occurrence: localOccurrence, isOwner });
  const layout = eventDetailLayout({ event: localEvent, occurrence: localOccurrence, isOwner });
  const participation = occurrenceParticipation(localOccurrence);
  // The viewer's series reminder, when they have a series RSVP.
  const seriesReminder = localEvent.my_rsvp ? myReminder(localEvent) : null;

  const seriesLabel =
    layout.repeatsLabel &&
    `${layout.repeatsLabel}${
      localEvent.recurrence_ends_at
        ? ` until ${formatDay(localEvent.recurrence_ends_at, { month: "short", day: "numeric", year: "numeric" })}`
        : ""
    }`;

  // Who's listed: the shown occurrence's guests (by effective status) once
  // read; the series RSVPs for the Event at its own dates or an older
  // payload. The backend decides who is listed — everyone for the creator,
  // other going guests for a viewer who's going, nobody otherwise.
  const guests = localOccurrence && participation ? localOccurrence.rsvps : localEvent.rsvps;
  const rsvpStats = guestStats(guests);

  const announce = (kind, id = localEvent.id) => announceEventChange({ eventId: id, kind });

  const handleUpdated = (updated) => {
    setLocalEvent(updated);
    onUpdated?.(updated);
  };

  // A request found the event or occurrence gone (deleted elsewhere): show
  // that instead of stale details, and let lists refetch.
  const markUnavailable = () => {
    setUnavailable(true);
    announce("updated");
  };

  // Event-form success. The occurrence in context is re-resolved from the
  // backend rather than kept stale (series edits can regenerate it).
  const handleEventEdited = async (updated) => {
    handleUpdated(updated);
    setNotice("");
    announce("updated", updated.id);
    if (!localOccurrence) return;
    try {
      setOccurrence(await findOccurrenceAfterEdit(updated, localOccurrence, token));
    } catch (err) {
      console.error("Failed to re-resolve occurrence after edit", err);
      setOccurrence(null);
    }
  };

  // Occurrence-form success: same occurrence (id and slot kept).
  const handleOccurrenceEdited = (updatedOccurrence) => {
    setOccurrence(updatedOccurrence);
    setNotice("");
    setMode("view");
    announce("updated");
  };

  // RSVP/reminder responses are the RSVP's { status, reminder } — the same
  // shape as my_rsvp — so they replace it directly. These are the SERIES
  // RSVP: the occurrence shown is re-resolved from it, keeping an answer or
  // reminder of its own (rebaseOnSeriesRsvp) — never overwritten by it.
  const applySeriesRsvp = (data) => {
    handleUpdated({ ...localEvent, my_rsvp: data });
    occurrenceSeq.current += 1;
    setLocalOccurrence((o) => (o && o.event_id === localEvent.id ? rebaseOnSeriesRsvp(o, data) : o));
  };
  // A confirmed series answer: Calendar and Home re-read, and the inbox
  // resolves the series invitation (never an occurrence one).
  const announceSeriesRsvp = (data) =>
    announceEventChange({ eventId: localEvent.id, kind: "updated", seriesRsvp: data });

  const handleRsvp = async (status) => {
    if (rsvpPending || rsvpStatus === status) return;

    setRsvpPending(true);
    setRsvpError("");
    try {
      const data = await submitRsvp(localEvent.id, status, token);
      applySeriesRsvp(data);
      announceSeriesRsvp(data);
      // The shown date inherits the answer unless it has its own; its guest
      // list may now be visible.
      refreshOccurrence(localOccurrence?.id);
    } catch (err) {
      console.error("Failed to update RSVP", err);
      if (isNotFound(err)) return markUnavailable();
      setRsvpError("Couldn't update your RSVP. Please try again.");
    } finally {
      setRsvpPending(false);
    }
  };

  const handleReminderChange = async (reminder) => {
    if (reminderPending || reminder === myReminder(localEvent)) return;

    setReminderPending(true);
    setReminderError("");
    try {
      const data = await updateReminder(localEvent.id, reminder, token);
      applySeriesRsvp(data);
    } catch (err) {
      console.error("Failed to update reminder", err);
      if (isNotFound(err)) return markUnavailable();
      setReminderError("Couldn't update your reminder. Please try again.");
    } finally {
      setReminderPending(false);
    }
  };

  // The one Reminder row for a date's participation (reminderEdit): the
  // series reminder by default, this date's own when it has one (the series
  // value clears it), never the RSVP status.
  const handleOccurrenceReminderChange = async (option) => {
    const edit = reminderEdit({ occurrence: localOccurrence, option, isOwner, seriesReminder });
    if (reminderPending || !edit) return;

    setReminderPending(true);
    setReminderError("");
    try {
      if (edit.target === "series") {
        applySeriesRsvp(await updateReminder(localEvent.id, edit.reminder, token));
        // This date inherits it: re-read its effective reminder.
        refreshOccurrence(localOccurrence.id);
      } else {
        const { occurrence: updated } = await answerOccurrence(localOccurrence, { reminder: edit.reminder }, token);
        setOccurrence(updated);
      }
    } catch (err) {
      console.error("Failed to update reminder", err);
      if (isNotFound(err)) return markUnavailable();
      setReminderError("Couldn't update your reminder. Please try again.");
    } finally {
      setReminderPending(false);
    }
  };

  // Change RSVP to Going: POST {"status": "going"} for this date only — the
  // series RSVP and this date's reminder are left alone. Stays open; the
  // guest list may now be visible.
  const handleChangeToGoing = async () => {
    if (changingToGoing || !localOccurrence) return;
    setChangingToGoing(true);
    setChangeToGoingError("");
    try {
      const { occurrence: updated } = await answerOccurrence(localOccurrence, { status: "going" }, token);
      setOccurrence(updated);
      refreshOccurrence(updated.id);
    } catch (err) {
      console.error("Failed to change RSVP to going", err);
      setChangeToGoingError(
        isNotFound(err) ? "This date is no longer available." : "Couldn't update your RSVP. Please try again."
      );
    } finally {
      setChangingToGoing(false);
    }
  };

  // One Invite Friends entry point. The scopes and the occurrence are fixed
  // when the friend picker opens, so the date can't change underneath it.
  const openInviteFriends = () => {
    const scopes = layout.inviteScopes;
    const target = localOccurrence;
    openModal("UserSelectModal", {
      onSend: (userIds) => {
        if (!userIds?.length) return;
        setInviteStatus("");
        if (scopes.length > 1) {
          // Nothing is sent until a scope is picked.
          setPendingInvite({ userIds, occurrence: target });
          setMode("choose-invite-scope");
        } else {
          handleInviteFriends(userIds);
        }
      },
    });
  };

  // Invites each picked friend at the one scope chosen — never both — then
  // re-reads who's listed. Every outcome is summarized (inviteSummary): new
  // and reopened invitations, no-ops, blocks, failures.
  const sendScopedInvites = async (scope) => {
    const { userIds, occurrence: target } = pendingInvite;
    setPendingInvite(null);
    setMode("view");
    setInviting(true);
    const outcomes = [];
    const ids = [...userIds].sort((a, b) => a - b);
    try {
      if (scope === INVITE_SCOPE.SERIES) {
        // The series endpoint answers 201 either way and leaves a pending or
        // going series RSVP as it is: those are no-ops, from a fresh read.
        let rsvps = localEvent.rsvps || [];
        try {
          rsvps = (await fetchEvent(localEvent.id, token)).rsvps || rsvps;
        } catch {
          // The copy shown stands in.
        }
        const alreadyIn = new Set(rsvps.filter((r) => r.status === "invited" || r.status === "going").map((r) => r.user));
        for (const userId of ids) {
          try {
            await inviteToEvent(localEvent.id, userId, token);
            outcomes.push(inviteOutcome({ invited: !alreadyIn.has(userId) }));
          } catch (err) {
            console.error("Failed to invite user to series", { eventId: localEvent.id, userId, err });
            outcomes.push(inviteOutcome({ error: err }));
          }
        }
        try {
          handleUpdated(await fetchEvent(localEvent.id, token));
        } catch {
          // Shown as before.
        }
      } else {
        for (const userId of ids) {
          try {
            const result = await inviteToOccurrence(target.id, userId, token);
            outcomes.push(inviteOutcome({ invited: result.invited }));
          } catch (err) {
            console.error("Failed to invite user to occurrence", { occurrenceId: target.id, userId, err });
            const outcome = inviteOutcome({ error: err });
            outcomes.push(outcome);
            if (outcome.kind === "canceled") break;
          }
        }
      }
      if (target && target.id === localOccurrence?.id) refreshOccurrence(target.id);
      setInviteStatus(inviteSummary(scope, outcomes).message);
    } finally {
      setInviting(false);
    }
  };

  // A one-off Event's flow (no scope to choose).
  const handleInviteFriends = async (selectedUserIds) => {
    if (!selectedUserIds?.length) return;

    setInviteStatus("");
    const failures = [];

    for (const userId of selectedUserIds) {
      try {
        await inviteToEvent(localEvent.id, userId, token);
      } catch (err) {
        console.error("Failed to invite user to event", { eventId: localEvent.id, userId, err });
        failures.push(userId);
      }
    }

    if (failures.length === 0) {
      setInviteStatus(
        `Invited ${selectedUserIds.length} friend${selectedUserIds.length === 1 ? "" : "s"}.`,
      );
    } else if (failures.length === selectedUserIds.length) {
      setInviteStatus("Couldn't send invites. Please try again.");
    } else {
      setInviteStatus("Some invites couldn't be sent. Please try again.");
    }
  };

  const startRemoval = (action) => {
    setRemovalError("");
    setPendingRemoval(action);
    setMode("confirm-removal");
  };

  const openRemoval = () => {
    if (actions.removals.length === 1) startRemoval(actions.removals[0]);
    else setMode("choose-removal");
  };

  // Runs the confirmed removal. Results come from the backend's responses
  // and refetches; nothing about the series is reconstructed here.
  const performRemoval = async () => {
    if (!pendingRemoval || removalInFlight.current) return;
    removalInFlight.current = true;
    setRemoving(true);
    setRemovalError("");

    try {
      switch (pendingRemoval) {
        case REMOVAL.CANCEL_OCCURRENCE: {
          // Stays shown, now canceled and read-only.
          const canceled = await cancelOccurrence(localOccurrence.id, token);
          setOccurrence(canceled);
          setNotice("");
          setMode("view");
          announce("updated");
          break;
        }
        case REMOVAL.DELETE_FUTURE: {
          const { event: updated, deleted_count: deletedCount } = await deleteEvent(localEvent.id, token, {
            scope: "future",
          });
          handleUpdated(updated);
          // The occurrence shown survives only if it had already started.
          let surviving = null;
          if (localOccurrence) {
            try {
              surviving = await fetchOccurrence(localOccurrence.id, token);
            } catch (err) {
              if (!isNotFound(err)) console.error("Failed to re-read occurrence", err);
            }
          }
          setOccurrence(surviving);
          setNotice(
            deletedCount > 0
              ? `Deleted ${deletedCount} upcoming occurrence${deletedCount === 1 ? "" : "s"}. The series has ended.`
              : "There were no upcoming occurrences to delete. The series has ended."
          );
          setMode("view");
          announce("trimmed");
          break;
        }
        case REMOVAL.DECLINE_OCCURRENCE: {
          // This date only; the series RSVP stays. It stays on Calendar
          // (canceled style) and here, offering Change RSVP to Going.
          const { occurrence: updated } = await answerOccurrence(localOccurrence, { status: "declined" }, token);
          setOccurrence(updated);
          setMode("view");
          refreshOccurrence(updated.id);
          break;
        }
        case REMOVAL.DECLINE_SERIES: {
          // The series RSVP only: dates answered on their own keep their
          // answers (the backend leaves occurrence RSVPs alone).
          const data = await submitRsvp(localEvent.id, "declined", token);
          applySeriesRsvp(data);
          announceSeriesRsvp(data);
          onClose?.();
          break;
        }
        case REMOVAL.DELETE_EVENT:
        case REMOVAL.REMOVE_FROM_CALENDAR: {
          if (pendingRemoval === REMOVAL.DELETE_EVENT) await deleteEvent(localEvent.id, token);
          else await deleteEventRsvp(localEvent.id, token);
          onRemoved?.(localEvent.id);
          announce("deleted");
          onClose?.();
          break;
        }
        default:
          break;
      }
    } catch (err) {
      console.error("Event removal failed", { action: pendingRemoval, err });
      const code = errorCode(err);
      if (code === "series_not_started") {
        // Nothing has happened yet: deleting the future would leave nothing.
        setSeriesNotStartedDetail(err.response.data.detail || "This series hasn't started yet.");
        setMode("series-not-started");
      } else if (isNotFound(err)) {
        if (pendingRemoval === REMOVAL.DELETE_EVENT || pendingRemoval === REMOVAL.REMOVE_FROM_CALENDAR) {
          // Already gone: the goal is met.
          onRemoved?.(localEvent.id);
          announce("deleted");
          onClose?.();
        } else {
          markUnavailable();
        }
      } else {
        if (code === "event_not_recurring") {
          // It stopped repeating elsewhere: show the current Event.
          fetchEvent(localEvent.id, token)
            .then(handleUpdated)
            .catch(() => {});
        }
        setRemovalError(eventErrorMessage(err));
      }
    } finally {
      removalInFlight.current = false;
      setRemoving(false);
    }
  };

  const panelClass = "bg-white rounded-lg shadow-lg w-full max-w-md p-5";

  if (mode === "edit-event" || mode === "edit-occurrence") {
    return (
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-md max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {mode === "edit-occurrence" ? (
          <OccurrenceEditForm
            event={localEvent}
            occurrence={localOccurrence}
            onSaved={handleOccurrenceEdited}
            onClose={() => setMode("view")}
          />
        ) : (
          <EventCreateForm
            event={localEvent}
            // Entire-series edits from a rescheduled occurrence open at the
            // series' own values (its custom time would leak into the series).
            occurrence={actions.role === "series" ? seriesEditOccurrence(localOccurrence) : localOccurrence}
            onSuccess={handleEventEdited}
            onClose={() => setMode("view")}
          />
        )}
      </div>
    );
  }

  if (mode === "choose-scope") {
    return (
      <div className={panelClass} onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold mb-1">Edit recurring event</h3>
        <p className="text-sm text-gray-600 mb-4">
          Change only this occurrence, or every occurrence in the series?
        </p>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => setMode("edit-occurrence")}
            className="rounded-full border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            This Occurrence
          </button>
          <button
            type="button"
            onClick={() => setMode("edit-event")}
            className="rounded-full border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            Entire Series
          </button>
          <button
            type="button"
            onClick={() => setMode("view")}
            className="text-xs text-gray-500 hover:text-blue-500 mt-1"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (mode === "choose-removal") {
    return (
      <div className={panelClass} onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold mb-3">Remove</h3>
        <div className="flex flex-col gap-2">
          {actions.removals.map((action) => (
            <button
              key={action}
              type="button"
              onClick={() => startRemoval(action)}
              className="rounded-full border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
            >
              {removalMenuLabel(action, actions.deleteEventLabel)}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setMode("view")}
            className="text-xs text-gray-500 hover:text-blue-500 mt-1"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (mode === "confirm-removal" && pendingRemoval) {
    const confirmation = removalConfirmation(pendingRemoval, actions.deleteEventLabel);
    return (
      <div
        role="alertdialog"
        aria-labelledby="event-removal-title"
        className={panelClass}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="event-removal-title" className="text-base font-semibold mb-1">
          {confirmation.title}
        </h3>
        <p className="text-sm text-gray-600 mb-4">{confirmation.message}</p>
        {removalError && <p className="text-xs text-red-600 mb-3">{removalError}</p>}
        <div className="flex gap-2">
          <button
            type="button"
            disabled={removing}
            onClick={() => setMode("view")}
            className="flex-1 rounded-full border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            {confirmation.dismissLabel}
          </button>
          <button
            type="button"
            disabled={removing}
            onClick={performRemoval}
            className="flex-1 rounded-full bg-red-600 px-3 py-1.5 text-sm text-white hover:bg-red-700 disabled:opacity-50"
          >
            {removing ? "Working…" : confirmation.confirmLabel}
          </button>
        </div>
      </div>
    );
  }

  if (mode === "series-not-started") {
    return (
      <div role="alertdialog" className={panelClass} onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold mb-1">Series Hasn't Started</h3>
        <p className="text-sm text-gray-600 mb-4">
          {seriesNotStartedDetail} Nothing of it has happened yet, so deleting its future would leave nothing.
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setMode("view")}
            className="flex-1 rounded-full border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => startRemoval(REMOVAL.DELETE_EVENT)}
            className="flex-1 rounded-full bg-red-600 px-3 py-1.5 text-sm text-white hover:bg-red-700"
          >
            {actions.deleteEventLabel}
          </button>
        </div>
      </div>
    );
  }

  if (mode === "choose-invite-scope" && pendingInvite) {
    const count = pendingInvite.userIds.length;
    const who = count === 1 ? "1 friend" : `${count} friends`;
    const date = formatInviteDate(effectiveOccurrence({ event: localEvent, occurrence: pendingInvite.occurrence }));
    return (
      <div role="alertdialog" aria-labelledby="invite-scope-title" className={panelClass} onClick={(e) => e.stopPropagation()}>
        <h3 id="invite-scope-title" className="text-base font-semibold mb-1">
          Send Invites
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Invite {who} to every date in the series, or only to {date}?
        </p>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => sendScopedInvites(INVITE_SCOPE.SERIES)}
            className="rounded-full border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            Invite to Series
          </button>
          {pendingInvite.occurrence && (
            <button
              type="button"
              onClick={() => sendScopedInvites(INVITE_SCOPE.OCCURRENCE)}
              className="rounded-full border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
            >
              Invite to This Date Only
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setPendingInvite(null);
              setMode("view");
            }}
            className="text-xs text-gray-500 hover:text-blue-500 mt-1"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (rsvpListStatus) {
    return (
      <GuestList
        stat={rsvpStats.find((s) => s.status === rsvpListStatus)}
        status={rsvpListStatus}
        onBack={() => setRsvpListStatus(null)}
      />
    );
  }

  const removalButtonLabel =
    actions.removals.length === 1
      ? removalMenuLabel(actions.removals[0], actions.deleteEventLabel)
      : isOwner
      ? "Delete…"
      : "Remove…";

  return (
    <div
      className="bg-white rounded-lg shadow-lg w-full max-w-md p-5 max-h-[85vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <EventDetailHeader
        details={details}
        hostedBy={localEvent.hosted_by}
        dateFootnote={actions.role === "retained" ? "From when this event repeated" : null}
        seriesLabel={seriesLabel}
      />

      {details.isCanceled && (
        <p className="text-xs text-gray-500 mb-4">
          This occurrence was canceled and can no longer be changed.
        </p>
      )}

      {notice && <p className="text-xs text-gray-600 mb-4">{notice}</p>}

      {/* Guests — listed only as the backend allows; hidden entirely when
          every category is empty. */}
      <GuestSummary stats={rsvpStats} onOpen={setRsvpListStatus} />

      {/* The series invitation, while pending. There's no "This Date" RSVP
          card: a guest declines this date or the series from the removal
          choices. */}
      {actions.showsInvitationResponse && (
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase text-gray-500 mb-2">
            {layout.showsOccurrenceParticipation ? "Entire Series" : "Your RSVP"}
          </p>
          <p className="text-xs text-gray-500 mb-2">
            You're invited — let {localEvent.hosted_by} know if you can make it.
          </p>
          <div className="flex gap-2">
            {RSVP_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                disabled={rsvpPending}
                onClick={() => handleRsvp(opt.value)}
                className="flex-1 rounded-full border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                {opt.label}
              </button>
            ))}
          </div>
          {rsvpError && <p className="text-xs text-red-600 mt-1">{rsvpError}</p>}
        </div>
      )}

      {/* The one Reminder row — per-user (it lives on the RSVP) and only fired
          by the backend for "going", so it's only offered then. For a date of
          a series it shows this date's effective reminder. */}
      {layout.reminderRow !== "none" && (
        <div className="mb-4">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <Bell size={14} className="text-gray-500" />
            <span>Reminder</span>
            <select
              value={
                layout.reminderRow === "occurrence"
                  ? localOccurrence.my_reminder ?? "none"
                  : myReminder(localEvent)
              }
              disabled={reminderPending}
              onChange={(e) =>
                layout.reminderRow === "occurrence"
                  ? handleOccurrenceReminderChange(e.target.value)
                  : handleReminderChange(e.target.value)
              }
              className="ml-auto border rounded px-2 py-1 text-sm disabled:opacity-50"
            >
              {REMINDER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          {layout.reminderRow === "occurrence" && (
            <ReminderFooter text={reminderFooter({ occurrence: localOccurrence, isOwner, seriesReminder, reminderLabel })} />
          )}
          {reminderError && <p className="text-xs text-red-600 mt-1">{reminderError}</p>}
        </div>
      )}

      {/* Organizer actions (mirrors backend's created_by-only enforcement) */}
      {(actions.edit || layout.inviteScopes.length > 0) && (
        <div className="flex gap-2 pt-3 border-t border-gray-200">
          {actions.edit && (
            <button
              type="button"
              onClick={() =>
                setMode(
                  actions.edit === "chooseScope"
                    ? "choose-scope"
                    : actions.edit === "occurrence"
                    ? "edit-occurrence"
                    : "edit-event"
                )
              }
              className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-full border hover:bg-gray-50"
            >
              <Pencil size={14} /> Edit
            </button>
          )}
          {layout.inviteScopes.length > 0 && (
            <button
              type="button"
              disabled={inviting}
              onClick={openInviteFriends}
              className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-full border hover:bg-gray-50 disabled:opacity-50"
            >
              <UserPlus size={14} /> {inviting ? "Sending…" : "Invite Friends"}
            </button>
          )}
        </div>
      )}
      {inviteStatus && <p className="text-xs text-gray-600 mt-2">{inviteStatus}</p>}

      {/* Removal — kept visually separate from the actions above. */}
      {actions.removals.length > 0 && (
        <div className="pt-3 mt-3 border-t border-gray-200">
          <button
            type="button"
            onClick={openRemoval}
            className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-full border border-red-300 text-red-600 hover:bg-red-50"
          >
            <Trash2 size={14} /> {removalButtonLabel}
          </button>
        </div>
      )}

      {/* A date declined on its own: change it back (this date only). */}
      {layout.offersChangeToGoing && (
        <div className="pt-3 mt-3 border-t border-gray-200">
          <button
            type="button"
            disabled={changingToGoing}
            onClick={handleChangeToGoing}
            className="w-full rounded-full bg-blue-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-600 disabled:opacity-50"
          >
            {changingToGoing ? "Working…" : "Change RSVP to Going"}
          </button>
          {changeToGoingError && <p className="text-xs text-red-600 mt-1">{changeToGoingError}</p>}
        </div>
      )}
    </div>
  );
}

function ReminderFooter({ text }) {
  return text ? <p className="text-xs text-gray-500 mt-1">{text}</p> : null;
}
